import { appConfig, buildAppFunctorNetworkErrorMessage } from '../config/appConfig';
import type { ApiEnvelope, AuthResponse, AuthUser } from '../types/domain';
import { resolveApiErrorMessage } from '../utils/authFormUtils';

const SESSION_STORAGE_KEY = 'psycheflow-webapp2.auth.session';
const CLOCK_SKEW_MS = 5000;
const REFRESH_MAX_ATTEMPTS = 3;
const REFRESH_RETRY_DELAYS_MS = [0, 600, 1800];

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function isLikelyNetworkErrorMessage(message: string): boolean {
  return /fetch failed|failed to fetch|network request failed|load failed|networkerror|aborterror|the network connection was lost|网络连接失败|无法连接/i.test(message);
}

function isTransientRefreshTransportError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }

  return error.name === 'AbortError'
    || /timeout of \d+ms exceeded/i.test(error.message)
    || isLikelyNetworkErrorMessage(error.message);
}

function shouldClearSessionOnRefreshFailure(response: Response | null): boolean {
  return response?.status === 401 || response?.status === 403;
}

function shouldRetryRefreshAttempt(
  response: Response | null,
  error: unknown,
  attemptIndex: number,
): boolean {
  if (attemptIndex >= REFRESH_MAX_ATTEMPTS - 1) {
    return false;
  }
  if (response?.status === 401 || response?.status === 403) {
    return false;
  }
  if (response?.status === 429 || (response && response.status >= 500)) {
    return true;
  }
  return isTransientRefreshTransportError(error);
}

type StoredSession = {
  accessToken?: string;
  refreshToken?: string;
  expiresAt?: number;
  user?: AuthUser | null;
};

type SessionListener = (session: StoredSession) => void;

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
  auth?: boolean;
  timeoutMs?: number;
};

function readStringField(value: unknown): string | undefined {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || undefined;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return undefined;
}

function summarizeRawErrorBody(text: string): string | undefined {
  const trimmed = text.trim();
  if (!trimmed) return undefined;

  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      return extractPayloadMessage(JSON.parse(trimmed));
    } catch {
      // fall through to plain-text handling
    }
  }

  if (/<\s*(?:html|head|title|body|center|h1)\b/i.test(trimmed)) {
    const title = trimmed.match(/<title[^>]*>([^<]+)<\/title>/i)?.[1]?.trim();
    const heading = trimmed.match(/<h1[^>]*>([^<]+)<\/h1>/i)?.[1]?.trim();
    const center = trimmed.match(/<center>([^<]+)<\/center>/i)?.[1]?.trim();
    const bits = [title, heading, center].filter((part, index, list) => {
      if (!part) return false;
      return list.findIndex((item) => item?.toLowerCase() === part.toLowerCase()) === index;
    });
    if (bits.length) return bits.join(' · ');
    return '网关返回了 HTML 错误页';
  }

  const compact = trimmed.replace(/\s+/g, ' ');
  return compact.length > 280 ? `${compact.slice(0, 280)}…` : compact;
}

function extractPayloadMessage(payload: unknown): string | undefined {
  if (payload == null) return undefined;
  if (typeof payload === 'string') {
    return summarizeRawErrorBody(payload);
  }
  if (typeof payload !== 'object') {
    return readStringField(payload);
  }

  const candidate = payload as Record<string, unknown>;
  const nestedError = candidate.error;
  const nestedErrorObject = nestedError && typeof nestedError === 'object'
    ? nestedError as Record<string, unknown>
    : null;

  const resolved = resolveApiErrorMessage(
    {
      error: readStringField(candidate.error),
      message: readStringField(candidate.message),
    },
    '',
  );
  if (resolved) return resolved;

  const nested = readStringField(nestedErrorObject?.message)
    || readStringField(nestedErrorObject?.error)
    || readStringField(nestedErrorObject?.detail)
    || readStringField(candidate.detail)
    || readStringField(candidate.reason)
    || readStringField(candidate.msg)
    || readStringField(candidate.description);

  if (nested) return nested;

  if (candidate.data != null && candidate.data !== payload) {
    const fromData = extractPayloadMessage(candidate.data);
    if (fromData) return fromData;
  }

  if (Array.isArray(candidate.errors) && candidate.errors.length > 0) {
    return extractPayloadMessage(candidate.errors[0]);
  }

  return undefined;
}

function httpStatusHint(status: number, path = ''): string | undefined {
  const shortPath = path.split('?')[0] || '';
  const isGeometryGen = /\/api\/dajiao\/generate-candidates$/i.test(shortPath);
  switch (status) {
    case 408:
      return '请求超时';
    case 413:
      return isGeometryGen
        ? '请求体过大（切块图请先上传 OSS 再试跑，勿直传 data URL）'
        : '请求体过大';
    case 502:
      return isGeometryGen
        ? '生图耗时过长，网关先断开了（多半是超时，不一定是模型挂了）'
        : '网关错误（上游无响应或超时）';
    case 503:
      return '服务暂时不可用';
    case 504:
      return isGeometryGen
        ? '生图网关超时（请稍后重试；成功结果有时已落库）'
        : '网关超时（上游处理时间过长）';
    default:
      return undefined;
  }
}

function formatHttpErrorMessage(status: number, payload: unknown, path: string): string {
  const detail = extractPayloadMessage(payload);
  const hint = httpStatusHint(status, path);
  const shortPath = path.split('?')[0];
  if (detail && hint) {
    return `${hint} · HTTP ${status}：${detail}`;
  }
  if (detail) {
    return `请求失败 · HTTP ${status}：${detail}`;
  }
  if (hint) {
    return `${hint} · HTTP ${status}：${shortPath}`;
  }
  return `请求失败 · HTTP ${status}：${shortPath}`;
}

function normalizeRequestError(error: unknown, path: string, timeoutMs?: number): Error {
  if (error instanceof Error) {
    if (error.name === 'AbortError') {
      const limit = timeoutMs ?? appConfig.defaultTimeoutMs;
      return new Error(`客户端请求超时（>${Math.round(limit / 1000)}s）：${path.split('?')[0]}`);
    }
    if (/fetch failed|network/i.test(error.message)) {
      return new Error(buildAppFunctorNetworkErrorMessage(path));
    }
    // 保留 HTTP 错误上挂的 details / errorCode，供工作台错误详情展示
    return error;
  }
  return new Error(buildAppFunctorNetworkErrorMessage(path));
}

async function parseResponsePayload(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

class ApiClient {
  private session: StoredSession = {};
  private bootstrapPromise: Promise<void> | null = null;
  private refreshPromise: Promise<string | undefined> | null = null;
  private sessionListeners = new Set<SessionListener>();

  subscribeSession(listener: SessionListener): () => void {
    this.sessionListeners.add(listener);
    return () => this.sessionListeners.delete(listener);
  }

  async bootstrap(): Promise<void> {
    if (this.bootstrapPromise) return this.bootstrapPromise;
    this.bootstrapPromise = (async () => {
      const raw = localStorage.getItem(SESSION_STORAGE_KEY);
      if (!raw) {
        this.emitSessionChanged();
        return;
      }
      try {
        const parsed = JSON.parse(raw) as StoredSession;
        this.session = {
          accessToken: typeof parsed.accessToken === 'string' ? parsed.accessToken : undefined,
          refreshToken: typeof parsed.refreshToken === 'string' ? parsed.refreshToken : undefined,
          expiresAt: typeof parsed.expiresAt === 'number' ? parsed.expiresAt : undefined,
          user: parsed.user ?? null,
        };
      } catch {
        this.session = {};
      }
      this.emitSessionChanged();
    })();
    return this.bootstrapPromise;
  }

  hasSession(): boolean {
    return Boolean(this.session.accessToken || this.session.refreshToken);
  }

  getCurrentUser(): AuthUser | null {
    return this.session.user ?? null;
  }

  async setCurrentUser(user: AuthUser | null): Promise<void> {
    this.session.user = user;
    await this.persistSession();
    this.emitSessionChanged();
  }

  async clearSession(): Promise<void> {
    this.session = {};
    localStorage.removeItem(SESSION_STORAGE_KEY);
    this.emitSessionChanged();
  }

  async storeAuthResult(result: AuthResponse): Promise<void> {
    this.session = {
      user: result.user ?? null,
      accessToken: result.accessToken,
      refreshToken: result.refreshToken,
      expiresAt: result.expiresIn ? Date.now() + result.expiresIn * 1000 - CLOCK_SKEW_MS : undefined,
    };
    await this.persistSession();
    this.emitSessionChanged();
  }

  async requestJson<T>(path: string, options: RequestOptions = {}): Promise<T> {
    await this.bootstrap();
    return this.performRequest<T>(path, options, false);
  }

  async getEnvelope<T>(path: string, auth: boolean = true, options: Pick<RequestOptions, 'timeoutMs'> = {}): Promise<T> {
    const payload = await this.requestJson<ApiEnvelope<T>>(path, { auth, ...options });
    if (!payload.success) {
      throw new Error(resolveApiErrorMessage(payload, '请求失败'));
    }
    return payload.data as T;
  }

  async postEnvelope<T>(
    path: string,
    body: unknown,
    auth: boolean = true,
    options: Pick<RequestOptions, 'timeoutMs'> = {},
  ): Promise<T> {
    const payload = await this.requestJson<ApiEnvelope<T>>(path, { method: 'POST', body, auth, ...options });
    if (!payload.success) {
      throw new Error(resolveApiErrorMessage(payload, '请求失败'));
    }
    return payload.data as T;
  }

  async patchEnvelope<T>(path: string, body: unknown, auth: boolean = true): Promise<T> {
    const payload = await this.requestJson<ApiEnvelope<T>>(path, { method: 'PATCH', body, auth });
    if (!payload.success) {
      throw new Error(resolveApiErrorMessage(payload, '请求失败'));
    }
    return payload.data as T;
  }

  async deleteEnvelope<T>(path: string, auth: boolean = true): Promise<T> {
    const payload = await this.requestJson<ApiEnvelope<T>>(path, { method: 'DELETE', auth });
    if (!payload.success) {
      throw new Error(resolveApiErrorMessage(payload, '请求失败'));
    }
    return payload.data as T;
  }

  async getAccessToken(): Promise<string | undefined> {
    await this.bootstrap();
    await this.ensureFreshAccessToken();
    return this.session.accessToken;
  }

  private async fetchWithTimeout(path: string, init: RequestInit, timeoutMs?: number): Promise<Response> {
    const controller = new AbortController();
    const effectiveTimeoutMs = timeoutMs ?? appConfig.defaultTimeoutMs;
    const timeout = setTimeout(() => controller.abort(), effectiveTimeoutMs);
    try {
      return await fetch(`${appConfig.apiBaseUrl}${path}`, { ...init, signal: controller.signal });
    } catch (error) {
      throw normalizeRequestError(error, path, effectiveTimeoutMs);
    } finally {
      clearTimeout(timeout);
    }
  }

  private async performRequest<T>(path: string, options: RequestOptions, retrying: boolean): Promise<T> {
    try {
      const auth = options.auth ?? true;
      if (auth) await this.ensureFreshAccessToken();

      const headers = new Headers(options.headers || {});
      headers.set('Accept', 'application/json');

      let body: BodyInit | undefined;
      const method = options.method || (options.body === undefined ? 'GET' : 'POST');

      if (options.body !== undefined) {
        if (
          typeof options.body === 'string'
          || options.body instanceof Blob
          || options.body instanceof FormData
          || options.body instanceof URLSearchParams
        ) {
          body = options.body;
        } else {
          headers.set('Content-Type', 'application/json');
          body = JSON.stringify(options.body);
        }
      }

      if (auth && this.session.accessToken) {
        headers.set(appConfig.accessHeaderName, `Bearer ${this.session.accessToken}`);
      }

      const response = await this.fetchWithTimeout(path, { method, headers, body }, options.timeoutMs);
      const payload = await parseResponsePayload(response);

      if (response.status === 401 && auth && !retrying && this.session.refreshToken) {
        const refreshed = await this.refreshAccessToken();
        if (refreshed) return this.performRequest<T>(path, options, true);
      }

      if (!response.ok) {
        const err = new Error(formatHttpErrorMessage(response.status, payload, path)) as Error & {
          statusCode?: number;
          details?: unknown;
          errorCode?: string;
          payload?: unknown;
        };
        err.statusCode = response.status;
        if (payload && typeof payload === 'object') {
          const body = payload as {
            details?: unknown;
            errorCode?: unknown;
            error?: unknown;
          };
          err.details = body.details !== undefined ? body.details : payload;
          if (typeof body.errorCode === 'string' && body.errorCode.trim()) {
            err.errorCode = body.errorCode.trim();
          } else if (typeof body.error === 'string' && /^[a-z0-9_]+$/i.test(body.error.trim())) {
            err.errorCode = body.error.trim();
          }
          err.payload = payload;
        }
        throw err;
      }

      return payload as T;
    } catch (error) {
      throw normalizeRequestError(error, path, options.timeoutMs);
    }
  }

  private hasValidAccessToken(): boolean {
    if (!this.session.accessToken) return false;
    if (!this.session.expiresAt) return true;
    return this.session.expiresAt > Date.now();
  }

  private async ensureFreshAccessToken(): Promise<string | undefined> {
    if (this.hasValidAccessToken()) return this.session.accessToken;
    if (!this.session.refreshToken) return this.session.accessToken;
    return this.refreshAccessToken();
  }

  private async refreshAccessToken(): Promise<string | undefined> {
    if (this.refreshPromise) return this.refreshPromise;
    if (!this.session.refreshToken) return undefined;

    this.refreshPromise = this.runRefreshWithRetries()
      .finally(() => {
        this.refreshPromise = null;
      });

    return this.refreshPromise;
  }

  private async runRefreshWithRetries(): Promise<string | undefined> {
    let lastError: unknown = null;

    for (let attempt = 0; attempt < REFRESH_MAX_ATTEMPTS; attempt += 1) {
      if (attempt > 0) {
        await delay(REFRESH_RETRY_DELAYS_MS[attempt] ?? 1800);
      }

      let response: Response | null = null;
      try {
        response = await this.fetchWithTimeout('/api/auth/refresh', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            [appConfig.accessHeaderName]: `Refresh ${this.session.refreshToken}`,
          },
          body: JSON.stringify({ refreshToken: this.session.refreshToken }),
        });

        const payload = (await parseResponsePayload(response)) as
          | (ApiEnvelope<never> & { accessToken?: string; refreshToken?: string; expiresIn?: number })
          | null;

        if (!response.ok || !payload?.accessToken || !payload.expiresIn) {
          const refreshMessage = extractPayloadMessage(payload) || '刷新鉴权失败';
          if (shouldClearSessionOnRefreshFailure(response)) {
            await this.clearSession();
          }
          throw new Error(refreshMessage);
        }

        this.session.accessToken = payload.accessToken;
        this.session.refreshToken = payload.refreshToken || this.session.refreshToken;
        this.session.expiresAt = Date.now() + payload.expiresIn * 1000 - CLOCK_SKEW_MS;
        await this.persistSession();
        this.emitSessionChanged();
        return this.session.accessToken;
      } catch (error) {
        lastError = error;
        if (!shouldRetryRefreshAttempt(response, error, attempt)) {
          return undefined;
        }
      }
    }

    if (import.meta.env.DEV && lastError) {
      console.warn('[auth] refresh 已达最大重试次数，保留本地 session', lastError);
    }
    return undefined;
  }

  private async persistSession(): Promise<void> {
    if (!this.session.accessToken && !this.session.refreshToken && !this.session.user) {
      localStorage.removeItem(SESSION_STORAGE_KEY);
      return;
    }
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(this.session));
  }

  private emitSessionChanged(): void {
    const snapshot: StoredSession = { ...this.session, user: this.session.user ?? null };
    for (const listener of this.sessionListeners) listener(snapshot);
  }
}

export const apiClient = new ApiClient();
